import subscriptionActions from "../subscription/subscriptionActions";
import {ajax} from "@timgroup/common-javascript-components";
import {hideNotifications, showNotifications} from "../notification/notificationActions";
import {logAction} from "../../logging/logEvent";
import Immutable from "immutable";
import {hideQuickEdit} from "../contribute/homepage/authorActions";

export const INITIAL_STATE = {
    loading: false,
    ideaId: null,
    displayed: false,
    data: null,
    currentTab: "view",
    history: {expanded: Immutable.Set()},
    updating: {
        inProgress: false,
        requestedPercentage: null,
        errors: [],
        outcomes: []
    },
    closing: {
        inProgress: false,
        error: "",
        closed: 0,
        selectedBucketIds: [],
        omissions: []
    }
};

export function reducer(state = INITIAL_STATE, action) {
    switch (action.type) {
        case "LOAD_IDEA_DETAILS":
            return {...INITIAL_STATE, ideaId: action.payload.ideaId, loading: true, displayed: true};
        case "SUBSCRIPTION_DATA_ARRIVED/IDEA_DETAILS":
            const bucketIds = action.payload.portfolios ? action.payload.portfolios.filter(p => p.open).map(p => p.bucketId) : [];

            return {
                ...state,
                loading: false,
                data: action.payload,
                closing: {...state.closing, selectedBucketIds: bucketIds}
            };
        case "UPDATE_IDEA_DETAILS_TAB":
            return {
                ...state,
                closing: {...state.closing, error: "", omissions: [], closed: 0},
                currentTab: action.payload
            };
        case "SELECT_ALL_BUCKET_IDS_TO_CLOSE":
            let allBucketIds = [];
            if (action.payload.selectAll) {
                allBucketIds = state.data.portfolios.filter(p => p.open).map(p => p.bucketId);
            }
            return {...state, closing: {...state.closing, selectedBucketIds: allBucketIds}};
        case "UPDATE_BUCKET_ID_TO_CLOSE":
            const include = action.payload.include;
            const bucketId = action.payload.bucketId;
            let newSelectedBucketIds;
            if (include) {
                newSelectedBucketIds = state.closing.selectedBucketIds.concat([bucketId])
            } else {
                const indexOf = state.closing.selectedBucketIds.indexOf(bucketId);
                newSelectedBucketIds = state.closing.selectedBucketIds.slice();
                newSelectedBucketIds.splice(indexOf, 1);
            }
            return {...state, closing: {...state.closing, selectedBucketIds: newSelectedBucketIds}};
        case "UPDATE_IDEA":
            return {...state, updating: {...state.updating, inProgress: true}};
        case "CLOSE_IDEA":
            return {...state, closing: {...state.closing, inProgress: true}};
        case "UPDATE_IDEA_FINISHED":
            return {
                ...state,
                updating: {
                    inProgress: false,
                    errors: action.payload.errors,
                    outcomes: action.payload.outcomes,
                    requestedPercentage: action.payload.requestedPercentage
                }
            };
        case "CLOSE_IDEA_FINISHED":
            if (action.payload.errors.length === 0) {
                return {
                    ...state,
                    currentTab: "view",
                    displayed: false,
                    closing: {
                        inProgress: false,
                        error: "",
                        selectedBucketIds: [],
                        closed: action.payload.closed,
                        omissions: action.payload.omissions
                    }
                };
            } else {
                return {
                    ...state, closing: {
                        ...state.closing,
                        inProgress: false,
                        closed: action.payload.closed,
                        error: action.payload.errors.join(" "),
                        omissions: action.payload.omissions
                    }
                };
            }
        case "EXPAND_HISTORY_ACTIVITY_PORTFOLIOS":
            return {
                ...state,
                history: {
                    ...state.history,
                    expanded: state.history.expanded.add(action.payload.activityId)
                }
            };
        case "COLLAPSE_HISTORY_ACTIVITY_PORTFOLIOS":
            return {
                ...state,
                history: {
                    ...state.history,
                    expanded: state.history.expanded.remove(action.payload.activityId)
                }
            };
        case "CLOSE_IDEA_DETAILS":
            return INITIAL_STATE;
        default:
            return state;
    }
}

export function loadIdeaDetails(ideaId) {
    return {type: "LOAD_IDEA_DETAILS", payload: {ideaId}};
}

export function closeIdeaDetails() {
    return {type: "CLOSE_IDEA_DETAILS"};
}

export function updateIdeaDetailsTab(newTab) {
    return {type: "UPDATE_IDEA_DETAILS_TAB", payload: newTab};
}

export function updateBucketIdToClose(bucketId, include) {
    return {type: "UPDATE_BUCKET_ID_TO_CLOSE", payload: {bucketId, include}};
}

export function selectAllBucketIdsToClose(selectAll) {
    return {type: "SELECT_ALL_BUCKET_IDS_TO_CLOSE", payload: {selectAll}};
}

export function beginUpdatingIdea(bucketIds) {
    return {type: "UPDATE_IDEA", payload: {bucketIds}};
}

export function beginClosingIdea(bucketIds) {
    return {type: "CLOSE_IDEA", payload: {bucketIds}};
}

export function finishedUpdatingIdea({errors, outcomes, requestedPercentage}) {
    return {type: "UPDATE_IDEA_FINISHED", payload: {errors, outcomes, requestedPercentage}};
}

export function finishedClosingIdea({errors, omissions, closed}) {
    return {type: "CLOSE_IDEA_FINISHED", payload: {errors, omissions, closed}};
}

export function expandHistoryActivityPortfolios(activityId) {
    return {type: "EXPAND_HISTORY_ACTIVITY_PORTFOLIOS", payload: {activityId}};
}

export function collapseHistoryActivityPortfolios(activityId) {
    return {type: "COLLAPSE_HISTORY_ACTIVITY_PORTFOLIOS", payload: {activityId}};
}

function delay(millis) {
    return new Promise(resolve => {
        setTimeout(resolve, millis);
    });
}

async function kickIdeaUntil(stopCondition) {
    let maxAttempts = 20;
    while (--maxAttempts >= 0) {
        const updated = await stopCondition();
        if (updated) {
            break;
        }
        await delay(250);
    }
}

function updateIdea(checkIdeaUpdated, portfolios, commentary, percentageChange) {
    return (dispatch, getState) => {
        const bucketIds = portfolios.map(p => p.bucketId);
        const onError = (errors, millisElapsed, millisBeforeAppearance) => {
            dispatch(finishedUpdatingIdea({
                errors,
                outcomes: [],
                requestedPercentage: percentageChange
            }));
            logAction({
                type: "IDEA_UPDATE_ERROR",
                bucketIds,
                errors,
                commentary,
                requestedPercentage: percentageChange,
                millisElapsed,
                millisBeforeAppearance
            });
        };

        dispatch(beginUpdatingIdea(bucketIds));
        const startRequestTimestamp = Date.now();
        ajax.post("/TradeIdeasMonitor/ui/api/updateIdea", {commentary, bucketIds, percentageChange}).subscribe(
            ({data}) => {
                const finishRequestTimestamp = Date.now();
                const {errors, requestedPercentage, outcomes} = data;

                if (errors && errors.length > 0) {
                    onError(errors, finishRequestTimestamp - startRequestTimestamp);
                    return;
                }

                const portfolioIdsThatWillUpdate = outcomes.filter(o => !o.failureMessage).map(o => o.portfolioId);

                const kickSubscription = subscriptionActions(dispatch).kick;
                const ideaUpdated = () => {
                    return checkIdeaUpdated(portfolioIdsThatWillUpdate, commentary, getState, kickSubscription);
                };
                kickIdeaUntil(ideaUpdated).then(() => {
                    dispatch(finishedUpdatingIdea({errors: [], outcomes, requestedPercentage}));
                    dispatch(showNotifications("update"));
                    dispatch(hideQuickEdit());
                    logAction({
                        type: "IDEA_UPDATED",
                        bucketIds,
                        commentary,
                        requestedPercentage,
                        outcomes,
                        millisElapsed: finishRequestTimestamp - startRequestTimestamp,
                        millisBeforeAppearance: Date.now() - startRequestTimestamp,
                    });
                }, error => {
                    onError([error], finishRequestTimestamp - startRequestTimestamp, Date.now() - startRequestTimestamp);
                });
            },
            error => {
                onError(["Unable to execute update (" + error.message + ")"]);
            }
        );
    };
}

function closeIdea(checkIdeaClosed, portfolios, commentary, source) {
    return (dispatch, getState) => {
        const bucketIds = portfolios.map(p => p.bucketId);

        dispatch(beginClosingIdea(bucketIds));
        const startRequestTimestamp = Date.now();
        ajax.post("/TradeIdeasMonitor/ui/api/closeIdeas", {source, commentary, bucketIds}).subscribe(
            ({data}) => {
                const finishRequestTimestamp = Date.now();
                const {bucketIdsRemainingOpen, failures = []} = data;

                const bucketIdsThatWillClose = new Set(bucketIds);
                for (const bucketId of bucketIdsRemainingOpen) {
                    bucketIdsThatWillClose.delete(bucketId);
                }

                const logIdeaClosed = (error) => {
                    const eventArrivedInTIM2Timestamp = Date.now();
                    const errors = error ? [error] : [];
                    logAction({
                        type: "IDEA_CLOSED",
                        bucketIds,
                        bucketIdsRemainingOpen,
                        failures,
                        errors,
                        commentary,
                        millisElapsed: finishRequestTimestamp - startRequestTimestamp,
                        millisBeforeAppearance: eventArrivedInTIM2Timestamp - startRequestTimestamp,
                    });
                };

                const kickSubscription = subscriptionActions(dispatch).kick;
                const ideaClosed = () => {
                    return !checkIdeaClosed || checkIdeaClosed(bucketIdsThatWillClose, getState, kickSubscription);
                };

                kickIdeaUntil(ideaClosed).then(() => {
                    const o = failures.map(f => ({
                        portfolio: portfolios.filter(p => p.bucketId === f.bucketId)[0].name,
                        reasons: f.messages
                    }));
                    dispatch(finishedClosingIdea({errors: [], closed: bucketIds.length - o.length, omissions: o}));
                    dispatch(showNotifications("close"));
                    logIdeaClosed();
                }, error => {
                    dispatch(finishedClosingIdea({errors: [error], closed: 0, omissions: []}));
                    logIdeaClosed(error);
                });
            },
            error => {
                dispatch(finishedClosingIdea({
                    errors: ["Something went wrong (" + error.message + ")"],
                    omissions: [],
                    closed: 0
                }));
            }
        );
    }
}

export default dispatch => ({
    showDetail(ideaId) {
        dispatch(loadIdeaDetails(ideaId));
        dispatch(hideNotifications());
    },

    close() {
        dispatch(closeIdeaDetails());
    },

    updateCurrentTab(newTab) {
        dispatch(updateIdeaDetailsTab(newTab));
    },

    updateIdea(checkIdeaUpdated, portfolios, commentary, percentageChange) {
        dispatch(updateIdea(checkIdeaUpdated, portfolios, commentary, percentageChange));
    },

    closeIdea(checkIdeaClosed, portfolios, commentary, source) {
        dispatch(closeIdea(checkIdeaClosed, portfolios, commentary, source));
    },

    finishedClosingIdea(state) {
        dispatch(finishedClosingIdea(state));
    },

    updateBucketIdToClose(bucketId, include) {
        dispatch(updateBucketIdToClose(bucketId, include))
    },

    selectAllBucketIdsToClose(selectAll) {
        dispatch(selectAllBucketIdsToClose(selectAll));
    },

    expandHistoryActivityPortfolios(activityId) {
        dispatch(expandHistoryActivityPortfolios(activityId));
    },

    collapseHistoryActivityPortfolios(activityId) {
        dispatch(collapseHistoryActivityPortfolios(activityId));
    }
});
